import React from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, Tooltip, XAxis, YAxis } from 'recharts';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import GoogleIcon from '@mui/icons-material/Google';
import StatCard from '../components/StatCard';
import ChartBlock from '../components/ChartBlock';
import TimelineChart from '../components/TimelineChart';
import DonutChart from '../components/DonutChart';
import styles from '../components/stats.module.css';
import palette, { scoreColor } from '../components/palette';
import SatisfactionResponses from './SatisfactionResponses';
import { useStatsPrint } from '../components/printContext';
import ChartTooltip from '../components/ChartTooltip';
import useElementWidth from '../components/useElementWidth';
import { formatDate, formatNumber, granularityHint } from '../utils/format';

export const SATISFACTION_CARDS = [
  { key: 'nps', label: 'NPS', icon: <SentimentSatisfiedAltOutlinedIcon />, accent: 'green', hint: 'de -100 a 100' },
  { key: 'responses', label: 'Respostas', icon: <ForumOutlinedIcon />, accent: 'blue' },
  { key: 'sent', label: 'Pesquisas enviadas', icon: <SendOutlinedIcon />, accent: 'purple' },
  { key: 'responseRate', label: 'Taxa de resposta', icon: <PercentOutlinedIcon />, type: 'percent', accent: 'purple' },
  { key: 'averageScore', label: 'Nota média', icon: <StarOutlineRoundedIcon />, type: 'decimal', accent: 'amber' },
];

export const categoryItems = (categories) => [
  { label: 'Promotores (9 e 10)', value: categories.promoters, color: palette.green },
  { label: 'Neutros (7 e 8)', value: categories.neutrals, color: palette.amber },
  { label: 'Detratores (0 a 6)', value: categories.detractors, color: palette.red },
];

const AXIS_TICK = { fontSize: 11, fill: palette.text };

/** Quantas respostas teve cada nota de 0 a 10, cada barra com a cor da categoria. */
export const ScoreDistribution = ({ distribution, animate = true }) => {
  const print = useStatsPrint();
  const [ref, width] = useElementWidth();
  if (!distribution.some((item) => item.count > 0)) {
    return <div className={styles.emptyChart}>Nenhuma resposta no período.</div>;
  }
  const rows = distribution.map((item) => ({ ...item, color: scoreColor(item.score) }));
  return (
    <div ref={ref} className={styles.chartArea} style={{ height: 190 }}>
      {width > 0 && (
        <BarChart width={width} height={190} data={rows} margin={{ top: 12, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke={palette.grid} />
          <XAxis dataKey="score" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: palette.grid }} />
          <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={32} allowDecimals={false} />
          <Tooltip
            cursor={{ fill: '#F3F6FE' }}
            content={(
              <ChartTooltip
                title={(label) => `Nota ${label}`}
                format={(value) => `${formatNumber(value)} ${value === 1 ? 'resposta' : 'respostas'}`}
              />
            )}
          />
          <Bar dataKey="count" name="Respostas" maxBarSize={32} radius={[6, 6, 0, 0]} isAnimationActive={animate && !print}>
            {rows.map((item) => <Cell key={item.score} fill={item.color} />)}
          </Bar>
        </BarChart>
      )}
    </div>
  );
};

export const Comments = ({ comments, linked = true }) => {
  const print = useStatsPrint();
  if (!comments || comments.length === 0) {
    return <div className={styles.emptyChart}>Nenhum comentário escrito no período.</div>;
  }
  return (
    <div className={styles.comments}>
      {comments.map((item, index) => (
        <div key={index} className={styles.comment}>
          <span className={styles.score} style={{ background: scoreColor(item.score) }}>{item.score}</span>
          <div className={styles.commentBody}>
            <span>{item.comment}</span>
            <span className={styles.commentMeta}>
              {linked && !print ? <Link to={`/patients/${item.patientId}`}>{item.name}</Link> : item.name}
              {' · '}
              {formatDate(item.date)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

const SatisfactionSection = ({ data, granularity }) => {
  // No PDF ficam só os comentários escritos; na tela, quem respondeu, com filtro e lista completa
  const print = useStatsPrint();
  return (
  <>
    <div className={styles.grid}>
      {SATISFACTION_CARDS.map((card) => <StatCard key={card.key} {...card} kpi={data.kpis[card.key]} />)}
      <StatCard
        label="Avaliações no Google"
        icon={<GoogleIcon />}
        accent="teal"
        kpi={{ value: data.google.completed, previous: null, change: null }}
        hint={`${formatNumber(data.google.invited)} ${data.google.invited === 1 ? 'convite enviado' : 'convites enviados'}`}
      />
    </div>

    <div className={styles.rowEven}>
      <ChartBlock title="Promotores, neutros e detratores">
        <DonutChart
          items={categoryItems(data.categories)}
          centerValue={data.kpis.nps.value ?? '—'}
          centerLabel="NPS"
          empty="Nenhuma resposta no período."
        />
      </ChartBlock>
      <ChartBlock title="Notas recebidas" hint="0 a 10">
        <ScoreDistribution distribution={data.distribution} />
      </ChartBlock>
    </div>

    <div className={styles.rowEven}>
      <ChartBlock title="NPS ao longo do período" hint={granularityHint(granularity)}>
        <TimelineChart
          data={data.timeline}
          granularity={granularity}
          kind="line"
          series={[{ key: 'nps', label: 'NPS', color: palette.green }]}
          height={200}
        />
      </ChartBlock>
      {print ? (
        <ChartBlock title="O que os pacientes escreveram" hint="mais recentes">
          <Comments comments={data.comments} />
        </ChartBlock>
      ) : (
        <ChartBlock title="Quem respondeu" hint="mais recentes primeiro">
          <SatisfactionResponses people={data.people} />
        </ChartBlock>
      )}
    </div>
  </>
  );
};

export default SatisfactionSection;
